<script setup lang="ts">
import useVuelidate from '@vuelidate/core';
import { useI18n } from 'vue-i18n';
import { useConfirm } from 'primevue/useconfirm';
import { storeToRefs } from 'pinia';
import { postOrder } from '../../dataProviders/user/orders';
import { updateAdminOrder } from '../../dataProviders/admin/orders';
import { useOrderStore } from '../../stores/orderForm';
import type { NewOrder } from '../../types/Order';
import { useUserStore } from '../../stores/user';

const emit = defineEmits<{ (e: 'close', refetch?: boolean): void, (e: 'delete', id: number): void }>();
const { profile } = useUserStore();
const store = useOrderStore();
const { order, isPreviewMode, disabledPreview, adminView, machineType } = storeToRefs(store);
const orderStore = useOrderStore();
const abortController = new AbortController();
onUnmounted(() => {
  store.resetStore();
  abortController.abort();
});

// Used to check the nested vuelidate instances
const rules = computed(() => ({}));
const v$ = useVuelidate(rules, order);

const filesRef = ref<any>(null);
async function handlePendingFiles() {
  const pendingFiles = filesRef.value.pendingFiles as Map<string, { file: File, meta: { progress: number, uploading: boolean, uploaded: boolean } }>;
  const fileStats: { progress: number, uploading: boolean, uploaded: boolean }[] = [];
  pendingFiles.forEach(entry => fileStats.push(entry.meta));
  const hasToUpload = fileStats.some(e => e.uploaded === false);
  if (hasToUpload) {
    setTimeout(() => {
      document.querySelector('#filesfieldset')?.scrollIntoView({ behavior: 'smooth' });
    }, 200);
    await filesRef.value.uploadFiles();
  }
}

watch(machineType, () => {
  v$.value.$reset();
});

const isUploading = ref(false);

async function onSubmit() {
  isUploading.value = true;
  const isValid = await v$.value.$validate();
  if (isValid) {
    await handlePendingFiles();
    const body = { ...order.value };
    let hasPassed = false;
    if (isPreviewMode.value) {
      hasPassed = await updateAdminOrder(body, abortController);
    }
    else {
      hasPassed = await postOrder(body as NewOrder, abortController);
    }
    if (hasPassed) {
      emit('close', true);
    }
  }
  else {
    setTimeout(() => {
      document.querySelector('.p-error')?.parentElement?.scrollIntoView({ behavior: 'smooth' });
    }, 200);
  }
  isUploading.value = false;
};

const { t } = useI18n();
const confirm = useConfirm();

function confirmCancelation(event: any, id: number) {
  confirm.require({
    target: event.currentTarget,
    icon: 'pi pi-exclamation-triangle',
    message: t('orders.confirmCancelation'),
    accept: () => {
      if (Number.isInteger(id)) {
        emit('delete', id);
      }
    },
  });
}

function adminDeleteOrder(id: number) {
  confirm.require({
    group: 'dialog',
    message: 'Сигурни ли сте, че желаете да продължите? Данните ще бъдат изтрити и няма да може да бъдат върнати.',
    header: 'Изтриване',
    icon: 'pi pi-exclamation-triangle',
    rejectClass: 'p-button-secondary p-button-outlined',
    acceptClass: 'p-button-danger',
    rejectLabel: 'Откажи',
    acceptLabel: 'Изтрий',
    accept: () => {
      if (Number.isInteger(id)) {
        emit('delete', id);
      }
    },
  });
}
</script>

<template>
  <form action="" :disabled="disabledPreview" @submit.prevent="onSubmit">
    <UserInfoFieldset v-if="adminView" />
    <FilesFieldset ref="filesRef" />
    <ManufactureFieldset />
    <DeliveryFieldset />
    <AdminFieldset />

    <div class="mt-12 flex justify-right gap-6">
      <Button
        v-if="adminView && profile.access_level === 'Admin'"
        size="small"
        severity="danger"
        outlined
        class="mr-auto"
        @click="adminDeleteOrder(order.id)"
      >
        {{ $t('action.delete') }}
      </Button>
      <Button
        outlined
        severity="secondary"
        size="small"
        @click="emit('close')"
      >
        {{ $t('action.close') }}
      </Button>
      <Button
        v-if="!disabledPreview"
        :icon="`pi ${isUploading ? 'pi-spin pi-spinner' : 'pi-send'}`"
        :label="$t('action.submit')"
        size="small"
        type="submit"
        :loading="orderStore.isFetchingResourcess"
        :disabled="orderStore.isFetchingResourcess"
      />

      <Button
        v-if="disabledPreview && order.status === 'pending'"
        outlined
        severity="warning"
        size="small"
        type="button"
        @click="confirmCancelation($event, order.id)"
      >
        <i class="pi pi-times mr-2" severity="secondary" />
        {{ $t('orders.action.cancel') }}
      </Button>
    </div>
  </form>
</template>
